/**
 * Evolu-backed ContactsDataSource implementation.
 *
 * Follows the DataSource interface pattern established in Phase 2
 * for testability. The interface defines the data shape; the Evolu
 * implementation handles persistence internally.
 *
 * Uses useRef for createQuery (PLANNING-RULES rule #3) and filters
 * out phantom rows with null name (PLANNING-RULES rule on upsert).
 *
 * @module features/contacts/hooks/useContactsDataSource
 */

import { useRef, useCallback, useMemo } from 'react';
import { useEvolu, useQuery } from '@evolu/react';
import { NonEmptyString1000, SqliteBoolean, getOrThrow } from '@evolu/common';
import type { Evolu } from '@evolu/common';
import type { SatsflowSchema } from '@/db/database';
import type { Contact, CreateContactData, UpdateContactData } from '@/types/contact';

/** DataSource interface for contact CRUD operations. */
export interface ContactsDataSource {
  /** All non-deleted contacts. */
  readonly contacts: readonly Contact[];
  /** Whether the initial query is still loading. */
  readonly isLoading: boolean;
  /** Create a new contact. */
  readonly create: (data: CreateContactData) => void;
  /** Update an existing contact by ID. */
  readonly update: (id: string, data: UpdateContactData) => void;
  /** Soft-delete a contact by ID. */
  readonly remove: (id: string) => void;
}

/**
 * Cast the generic Evolu instance to the app's typed schema.
 *
 * @param evolu - Generic Evolu instance from useEvolu()
 * @returns Typed Evolu instance for SatsflowSchema
 */
function typedEvolu(evolu: ReturnType<typeof useEvolu>): Evolu<SatsflowSchema> {
  return evolu as unknown as Evolu<SatsflowSchema>;
}

/**
 * Convert a string to NonEmptyString1000 branded type.
 *
 * @param value - String value
 * @returns Branded NonEmptyString1000
 */
function toNes(value: string): NonEmptyString1000 {
  return getOrThrow(NonEmptyString1000.from(value)) as NonEmptyString1000;
}

/**
 * Convert a string or undefined to nullable NonEmptyString1000.
 *
 * @param value - String value or undefined
 * @returns Branded NonEmptyString1000 or null
 */
function toNullableNes(value: string | undefined | null): NonEmptyString1000 | null {
  if (value === undefined || value === null) return null;
  return toNes(value);
}

/**
 * Evolu-backed ContactsDataSource hook.
 *
 * Queries all non-deleted contacts from the Evolu contact table,
 * maps to application types, and provides CRUD operations.
 *
 * Tier 2 (active user feature).
 *
 * @returns ContactsDataSource with contacts array and CRUD methods
 */
export function useContactsDataSource(): ContactsDataSource {
  const evolu = useEvolu();
  const db = typedEvolu(evolu);

  // createQuery wrapped in useRef to prevent subscription tracking breakage
  const queryRef = useRef(
    db.createQuery((q) =>
      q
        .selectFrom('contact')
        .select([
          'id',
          'name',
          'avatarUrl',
          'lightningAddress',
          'bolt12Offer',
          'cashuAddress',
          'nostrPubkey',
          'notes',
          'isFavorite',
          'lastTransactionAt',
          'transactionCount',
          'isVerified',
          'trustLevel',
          'isDeleted',
        ])
        .orderBy('name', 'asc'),
    ),
  );

  const rows = useQuery(queryRef.current);

  // Filter out deleted rows (SqliteBoolean uses 0/1, not JS boolean) and
  // phantom rows with null name (Evolu upsert bug protection)
  const contacts: readonly Contact[] = useMemo(
    () =>
      rows
        .filter((row) => row.name !== null && row.isDeleted !== 1)
        .map((row) => ({
          id: String(row.id),
          name: String(row.name),
          avatarUrl: row.avatarUrl != null ? String(row.avatarUrl) : null,
          lightningAddress: row.lightningAddress != null ? String(row.lightningAddress) : null,
          bolt12Offer: row.bolt12Offer != null ? String(row.bolt12Offer) : null,
          cashuAddress: row.cashuAddress != null ? String(row.cashuAddress) : null,
          nostrPubkey: row.nostrPubkey != null ? String(row.nostrPubkey) : null,
          notes: row.notes != null ? String(row.notes) : null,
          isFavorite: (row.isFavorite as unknown as number) === 1,
          lastTransactionAt: row.lastTransactionAt != null ? String(row.lastTransactionAt) : null,
          transactionCount: (row.transactionCount as number) ?? 0,
          isVerified: (row.isVerified as unknown as number) === 1,
          trustLevel: String(row.trustLevel ?? 'known'),
        })),
    [rows],
  );

  const create = useCallback(
    (data: CreateContactData): void => {
      db.insert('contact', {
        name: toNes(data.name),
        lightningAddress: toNullableNes(data.lightningAddress) as never,
        bolt12Offer: toNullableNes(data.bolt12Offer) as never,
        cashuAddress: toNullableNes(data.cashuAddress) as never,
        nostrPubkey: toNullableNes(data.nostrPubkey) as never,
        notes: toNullableNes(data.notes) as never,
        isFavorite: 0 as unknown as SqliteBoolean,
        transactionCount: 0,
        isVerified: 0 as unknown as SqliteBoolean,
        isRecoveryContact: 0 as unknown as SqliteBoolean,
        trustLevel: toNes('known'),
      });
    },
    [db],
  );

  const update = useCallback(
    (id: string, data: UpdateContactData): void => {
      const updates: Record<string, unknown> = { id: id as never };

      if (data.name !== undefined) {
        updates.name = toNes(data.name);
      }
      if (data.lightningAddress !== undefined) {
        updates.lightningAddress = toNullableNes(data.lightningAddress);
      }
      if (data.bolt12Offer !== undefined) {
        updates.bolt12Offer = toNullableNes(data.bolt12Offer);
      }
      if (data.cashuAddress !== undefined) {
        updates.cashuAddress = toNullableNes(data.cashuAddress);
      }
      if (data.nostrPubkey !== undefined) {
        updates.nostrPubkey = toNullableNes(data.nostrPubkey);
      }
      if (data.notes !== undefined) {
        updates.notes = toNullableNes(data.notes);
      }
      if (data.avatarUrl !== undefined) {
        updates.avatarUrl = toNullableNes(data.avatarUrl);
      }
      if (data.isFavorite !== undefined) {
        updates.isFavorite = (data.isFavorite ? 1 : 0) as unknown as SqliteBoolean;
      }

      db.update('contact', updates as never);
    },
    [db],
  );

  const remove = useCallback(
    (id: string): void => {
      // Soft-delete: set isDeleted to 1 (SqliteBoolean)
      db.update('contact', {
        id: id as never,
        isDeleted: 1 as unknown as SqliteBoolean,
      });
    },
    [db],
  );

  return {
    contacts,
    isLoading: false,
    create,
    update,
    remove,
  };
}
